/**
 * Carbon tooltip: an inverse bubble (text-inverse on inverse) that opens on
 * hover and on keyboard focus, and closes on Escape. It replaces ad-hoc
 * `title=` tooltips. The bubble is always in the DOM (hidden while closed,
 * next to its trigger), so the trigger's aria-describedby resolves in every
 * state. Open, it renders through a headless UI Portal at a fixed position
 * taken from the trigger (useAnchoredPosition): no `overflow` ancestor, such
 * as the DataTable's horizontal scroller, can clip it, it flips side near a
 * viewport edge, and it stays 16px inside the viewport, so it never widens a
 * phone page.
 *
 * `interactive` (default) wraps the trigger in a button, so keyboard users
 * reach it; `definition` adds Carbon's dotted underline. Set
 * `interactive={false}` when the trigger already sits inside a button or a
 * link (a pill inside a clickable row): it then opens on hover only and never
 * nests one control in another.
 */
import { useId, useState, type KeyboardEvent } from 'react'
import { Portal } from '@headlessui/react'
import clsx from 'clsx'
import { useAnchoredPosition } from './useAnchoredPosition'
import type { TooltipProps } from './types'

export function Tooltip({
  label,
  children,
  align = 'center',
  side = 'bottom',
  interactive = true,
  definition = false,
  className,
}: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null)
  const [bubbleEl, setBubbleEl] = useState<HTMLSpanElement | null>(null)
  const style = useAnchoredPosition(anchor, bubbleEl, open, { side, align, gap: 8, edge: 16 })

  const show = () => setOpen(true)
  const hide = () => setOpen(false)
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      hide()
    }
  }

  const triggerClass = clsx(
    'inline-flex items-center border-0 bg-transparent p-0 text-left text-inherit',
    definition && 'border-b border-dotted border-line-strong',
  )

  const bubble = (
    <span
      ref={setBubbleEl}
      id={id}
      role="tooltip"
      data-menu=""
      hidden={!open}
      style={style}
      className={clsx(
        'z-[80] w-max max-w-[min(18rem,calc(100vw-32px))] whitespace-normal bg-inverse px-4 py-2',
        'text-left text-body font-normal normal-case tracking-[0.16px] text-[color:var(--text-inverse)]',
        !open && 'absolute',
      )}
    >
      {label}
    </span>
  )

  return (
    <span ref={setAnchor} className={clsx('relative inline-flex', className)} onMouseEnter={show} onMouseLeave={hide}>
      {interactive ? (
        <button type="button" aria-describedby={id} onFocus={show} onBlur={hide} onKeyDown={onKeyDown} className={triggerClass}>
          {children}
        </button>
      ) : (
        <span aria-describedby={id} className={triggerClass}>
          {children}
        </span>
      )}
      {open ? <Portal>{bubble}</Portal> : bubble}
    </span>
  )
}

export default Tooltip
